import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import RichTextRenderer from '../common/RichTextRenderer';

const easing = [0.22, 1, 0.36, 1];

const ReinforcementPopup = ({ isOpen, content, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && content && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
        >
          <motion.div
            className="bg-background w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden"
            style={{ borderRadius: '4px', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.4), 0 2px 0 hsl(var(--border))' }}
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.32, ease: easing }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Eyebrow band — no candy gradient, just a stamp */}
            <div className="flex-shrink-0 px-6 sm:px-8 pt-6 pb-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
                  Pause · Reinforcement
                </span>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full text-foreground/55 hover:text-foreground hover:bg-foreground/5 flex items-center justify-center transition-colors"
                aria-label="Close reinforcement"
                title="Close and continue"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Title */}
            <div className="flex-shrink-0 px-6 sm:px-8 pb-5">
              <h4
                className="font-display font-bold text-foreground leading-tight"
                style={{ fontSize: 'clamp(20px, 2.4vw, 26px)', letterSpacing: '-0.015em' }}
              >
                {content.title}
              </h4>
              <div className="mt-4 h-px bg-border" />
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 sm:px-8 pb-6 min-h-0">
              <RichTextRenderer
                content={content.content}
                className="lesson-content"
                fontSize="16px"
                lineHeight="1.7"
                maxWidth="none"
                allowInlineStyles={true}
              />
            </div>

            {/* Footer — quiet caption */}
            <div className="flex-shrink-0 px-6 sm:px-8 py-3.5 border-t border-border">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/55 text-center">
                Close to continue
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ReinforcementPopup;
